import { perBarOutlines } from './barOutlines';
import {
    CHART_IN_PROGRESS_BAR_BORDER_WIDTH,
    CHART_IN_PROGRESS_FILL_FACTOR,
    CHART_IN_PROGRESS_POINT_BORDER_WIDTH,
    CHART_IN_PROGRESS_POINT_RADIUS,
    CHART_POINT_BORDER_WIDTH,
    CHART_SERIES_DASH_PATTERN,
    CHART_TRANSPARENT_COLOR,
} from './constants';
import type { MetricSeriesSpec } from './seriesTypes';
import type { MetricChartKind, MetricChartSpec } from './types';

type InProgressSpec = Pick<MetricChartSpec<MetricChartKind>, 'days' | 'inProgress'>;

/** Position of the in-progress bucket on the axis, or -1 when the spec marks none there. */
export function inProgressIndex(spec: InProgressSpec): number {
    return spec.inProgress ? spec.days.indexOf(spec.inProgress.key) : -1;
}

/** The in-progress note for the hovered position, or nothing. */
export function inProgressTooltipLines(spec: InProgressSpec, index: number): string[] {
    return spec.inProgress && spec.days[index] === spec.inProgress.key
        ? [spec.inProgress.tooltip]
        : [];
}

const HEX_COLOR = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i;
const RGB_COLOR = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i;

function alphaText(alpha: number): string {
    return String(Math.round(alpha * 1000) / 1000);
}

/**
 * `color` at `factor` of its own opacity.
 *
 * Reads the two forms the chart palettes use, `#rrggbb[aa]` and `rgb[a](...)`, so a muted
 * palette colour fades from its own opacity rather than from full. Anything else comes
 * back unchanged: the outline then marks the bar alone, which beats guessing a colour.
 */
export function fadeColor(color: string, factor: number): string {
    const hex = HEX_COLOR.exec(color);
    if (hex) {
        const [red, green, blue] = [hex[1], hex[2], hex[3]].map((part) =>
            Number.parseInt(part, 16)
        );
        const alpha = hex[4] === undefined ? 1 : Number.parseInt(hex[4], 16) / 255;
        return `rgba(${red}, ${green}, ${blue}, ${alphaText(alpha * factor)})`;
    }
    const rgb = RGB_COLOR.exec(color);
    if (rgb) {
        const alpha = rgb[4] === undefined ? 1 : Number(rgb[4]);
        return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alphaText(alpha * factor)})`;
    }
    return color;
}

export interface InProgressBarStyle {
    backgroundColor: string[];
    borderColor: string[];
    borderWidth: number[];
}

/**
 * A bar dataset's fill and outline with the in-progress bar faded inside a solid outline
 * of its own colour. `fills` is each bar's resolved colour, so a bar whose colour carries
 * a threshold meaning keeps that hue and only loses weight.
 *
 * A bar's own `barOutlines` entry marks a state, so it survives on every bar, the marked
 * one included: there the fade alone says the bucket is in progress, and the outline
 * still says what the value means.
 */
export function inProgressBarStyle(
    series: MetricSeriesSpec,
    fills: string[],
    index: number
): InProgressBarStyle {
    const border = series.barBorder;
    const outlines = series.barOutlines
        ? perBarOutlines(series, series.barOutlines, fills.length)
        : null;
    const ownOutline = (position: number): boolean =>
        series.barOutlines?.colors[position] !== undefined;
    const marked = (position: number): boolean => position === index && !ownOutline(position);
    return {
        backgroundColor: fills.map((fill, position) =>
            position === index ? fadeColor(fill, CHART_IN_PROGRESS_FILL_FACTOR) : fill
        ),
        borderColor: fills.map((fill, position) =>
            marked(position)
                ? fill
                : (outlines?.borderColor[position] ?? border?.color ?? CHART_TRANSPARENT_COLOR)
        ),
        borderWidth: fills.map((_fill, position) =>
            marked(position)
                ? CHART_IN_PROGRESS_BAR_BORDER_WIDTH
                : (outlines?.borderWidth[position] ?? border?.width ?? 0)
        ),
    };
}

/** The segment context Chart.js passes to a scriptable `segment` option. */
interface SegmentContext {
    p1DataIndex: number;
}

export interface InProgressPointStyle {
    pointRadius: number[];
    pointBackgroundColor: string[];
    pointBorderColor: string;
    pointBorderWidth: number[];
    segment: { borderDash: (context: SegmentContext) => [number, number] | undefined };
}

/**
 * A line dataset's points with the in-progress one hollow and enlarged, and the segment
 * into it dashed, so a partial reading is not taken for a complete one. `radii` is what
 * the series would use otherwise: per point for a line, one radius for a scatter.
 * `hollowFill` is the chart's surface, which is what makes the point read as hollow.
 */
export function inProgressPointStyle(
    color: string,
    radii: number[] | number,
    length: number,
    index: number,
    hollowFill: string
): InProgressPointStyle {
    const radiusAt = (position: number): number =>
        typeof radii === 'number' ? radii : (radii[position] ?? 0);
    const positions = Array.from({ length }, (_value, position) => position);
    return {
        pointRadius: positions.map((position) =>
            position === index ? CHART_IN_PROGRESS_POINT_RADIUS : radiusAt(position)
        ),
        pointBackgroundColor: positions.map((position) =>
            position === index ? hollowFill : color
        ),
        pointBorderColor: color,
        pointBorderWidth: positions.map((position) =>
            position === index ? CHART_IN_PROGRESS_POINT_BORDER_WIDTH : CHART_POINT_BORDER_WIDTH
        ),
        // Undefined falls back to the dataset's own dash, so a dashed plan line stays dashed.
        segment: {
            borderDash: (context) =>
                context.p1DataIndex === index ? CHART_SERIES_DASH_PATTERN : undefined,
        },
    };
}
